import * as React from 'react';
import { cn, Icon } from '@ideoz/ui';

export interface InfoSectionProps extends React.HTMLAttributes<HTMLDivElement> {
  title: string;
  description: string;
  iconName?: any;
  variant?: 'info' | 'warning' | 'error' | 'success';
}

const variantStyles = {
  info: {
    container: 'bg-blue-50 dark:bg-blue-950 border-blue-200 dark:border-blue-800',
    icon: 'text-blue-700 dark:text-blue-300',
    title: 'text-blue-700 dark:text-blue-300 font-semibold',
    description: 'text-blue-700 dark:text-blue-300',
    // Icon color: use currentColor so it inherits from the icon text class
    color: 'currentColor',
  },
  warning: {
    container: 'bg-warning-50 dark:bg-warning-950 border-warning-200 dark:border-warning-800',
    icon: 'text-warning-600 dark:text-warning-400',
    title: 'text-warning-900 dark:text-warning-100 font-semibold',
    description: 'text-warning-700 dark:text-warning-300',
    color: 'currentColor',
  },
  error: {
    container: 'bg-error-50 dark:bg-error-950 border-error-200 dark:border-error-800',
    icon: 'text-error-600 dark:text-error-400',
    title: 'text-error-900 dark:text-error-100 font-semibold',
    description: 'text-error-700 dark:text-error-300',
    color: 'currentColor',
  },
  success: {
    container: 'bg-success-50 dark:bg-success-950 border-success-200 dark:border-success-800',
    icon: 'text-success-600 dark:text-success-400',
    title: 'text-success-900 dark:text-success-100 font-semibold',
    description: 'text-success-700 dark:text-success-300',
    color: 'currentColor',
  },
};

const InfoSection = React.forwardRef<HTMLDivElement, InfoSectionProps>(
  (
    { className, title, description, iconName = 'lucide_info', variant = 'info', ...props },
    ref
  ) => {
    const styles = variantStyles[variant];

    return (
      <div
        ref={ref}
        className={cn('flex gap-3 p-3 rounded-lg border', styles.container, className)}
        {...props}
      >
        <div className={cn("flex-shrink-0 mt-0.5", styles.icon)}>
          <Icon icon={iconName} size={18} strokeWidth={2} color={styles.color} />
        </div>
        <div className="space-y-1">
          <h4 className={cn('text-sm font-medium', styles.title)}>{title}</h4>
          <p className={cn('text-sm', styles.description)}>{description}</p>
        </div>
      </div>
    );
  }
);
InfoSection.displayName = 'InfoSection';

export { InfoSection };
